import { Outlet, NavLink } from 'react-router-dom'
import { useAuthStore } from '../../hooks/useAuth'
import { useState, useEffect, Suspense } from 'react'

function ContentLoader() {
  return (
    <div className="flex-1 flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-aqua-200 border-t-aqua-500 rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-semibold">Cargando...</p>
      </div>
    </div>
  )
}
import {
  ShoppingCart, Package, Users, Store, Heart,
  Menu, LogOut, Smartphone, Bell, WifiOff, ClipboardList, Truck, PackageCheck, UserCircle, Clock, Boxes, TrendingUp, Building2, CheckSquare, GraduationCap, Ticket,
} from 'lucide-react'
import clsx from 'clsx'

const EcoIcon = ({ size = 20 }: { size?: number }) => (
  <img src="/eco-canje.png" alt="Eco Canje" style={{ width: size, height: size }} className="object-contain" />
)
import PulpinWidget from '../ui/PulpinWidget'
import OfflineBanner from '../ui/OfflineBanner'
import { useNetworkStatus } from '../../hooks/useNetworkStatus'
import { useOfflineQueue } from '../../hooks/useOfflineQueue'
import { usePWA } from '../../hooks/usePWA'
import { useFCM } from '../../hooks/useFCM'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../lib/api'
import toast from 'react-hot-toast'

const BASE_NAV = [
  { label: 'Tareas',      to: '/colab/tareas',      icon: CheckSquare },
  { label: 'Ventas',      to: '/colab/ventas',      icon: ShoppingCart },
  { label: 'Pedidos',     to: '/colab/pedidos',     icon: ClipboardList },
  { label: 'Recepción',   to: '/colab/recepcion',   icon: Truck },
  { label: 'Despachos',   to: '/colab/despachos',   icon: PackageCheck },
  { label: 'Inventario',  to: '/colab/inventario',  icon: Package },
  { label: 'Clientes',    to: '/colab/clientes',    icon: Users },
  { label: 'Mi Sede',     to: '/colab/sede',         icon: Store },
  { label: 'Insumos',     to: '/colab/insumos',       icon: Boxes },
  { label: 'Activos',     to: '/colab/activos',       icon: Building2, permiso: 'EDITAR_ACTIVOS' },
  { label: 'Mayorista',   to: '/colab/mayorista',     icon: TrendingUp, permiso: 'VENTAS_MAYORISTA' },
  { label: 'Eco Canje',  to: '/colab/eco',           icon: EcoIcon },
  { label: 'Rifa',        to: '/colab/rifa',          icon: Ticket },
  { label: 'Capacitación', to: '/colab/capacitacion',  icon: GraduationCap },
  { label: 'Mi Perfil',   to: '/colab/perfil',        icon: UserCircle },
]

export default function ColabLayout() {
  const { user, logout } = useAuthStore()
  const [open, setOpen] = useState(false)
  // Filtrar nav según permisos del usuario
  const navItems = BASE_NAV.filter(item => !item.permiso || user?.permisos?.includes(item.permiso))
  const { isOnline } = useNetworkStatus()
  const { pendingCount } = useOfflineQueue()
  const { canInstall, installPWA } = usePWA()
  const { permission, requestPermission } = useFCM()
  const sedeLabel = user?.sede === 'TORO' ? 'Toro' : 'Pereira'
  const qc = useQueryClient()

  // Widget de fichar
  const { data: asistenciaHoy } = useQuery<any>({
    queryKey: ['asistencia-hoy'],
    queryFn: () => api.get('/asistencia/hoy').then(r => r.data),
    refetchInterval: 60_000,
    enabled: !!user,
  })

  const mutEntrada = useMutation({
    mutationFn: () => api.post('/asistencia/entrada').then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['asistencia-hoy'] })
      toast.success(data.estado === 'TARDE' ? 'Entrada registrada (tarde)' : 'Entrada registrada a tiempo')
    },
    onError: () => toast.error('Error al registrar entrada'),
  })

  const mutSalida = useMutation({
    mutationFn: () => api.post('/asistencia/salida').then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asistencia-hoy'] })
      toast.success('Salida registrada')
    },
    onError: () => toast.error('Error al registrar salida'),
  })

  // Badge de pedidos activos
  const { data: pedidosActivos = [] } = useQuery<any[]>({
    queryKey: ['colab-pedidos-count', user?.sede],
    queryFn: () => api.get('/pedidos', { params: { sede: user?.sede } }).then(r =>
      r.data.filter((p: any) => ['PENDIENTE', 'CONFIRMADO', 'EN_PREPARACION', 'EN_CAMINO'].includes(p.estado))
    ),
    refetchInterval: 30_000,
    enabled: !!user,
  })
  const pedidosCount = pedidosActivos.length

  // Mostrar banner de instalar solo en sidebar mobile
  const [showInstallBanner, setShowInstallBanner] = useState(false)
  useEffect(() => {
    if (canInstall) {
      const dismissed = localStorage.getItem('aqua_install_dismissed')
      if (!dismissed) setShowInstallBanner(true)
    }
  }, [canInstall])

  const dismissInstall = () => {
    localStorage.setItem('aqua_install_dismissed', '1')
    setShowInstallBanner(false)
  }

  const handleInstall = async () => {
    const ok = await installPWA()
    if (ok) setShowInstallBanner(false)
  }

  return (
    <div className="flex min-h-screen bg-aqua-50">
      <aside className={clsx(
        'fixed inset-y-0 left-0 z-50 w-60 bg-aqua-500 flex flex-col transition-transform duration-250 lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="px-5 py-5 border-b border-white/15">
          <img src="/logo-aqua.png" alt="Aqua" className="h-10 w-auto brightness-0 invert" />
          <div className="text-xs font-bold uppercase tracking-widest text-white/40 mt-0.5">
            Sede {sedeLabel}
          </div>
          {!isOnline && (
            <div className="mt-2 flex items-center gap-1.5 text-orange-300 text-xs font-semibold">
              <WifiOff size={12} /> Modo offline
            </div>
          )}
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(({ label, to, icon: Icon }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) => clsx(
                'flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all',
                isActive ? 'bg-white/20 text-white' : 'text-white/65 hover:bg-white/10 hover:text-white'
              )}
              onClick={() => setOpen(false)}
            >
              <Icon size={17} />
              {label}
              {label === 'Ventas' && pendingCount > 0 && (
                <span className="ml-auto bg-orange-400 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
              {label === 'Pedidos' && pedidosCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                  {pedidosCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Banner instalar PWA */}
        {showInstallBanner && (
          <div className="mx-3 mb-2 bg-white/10 rounded-xl p-3 border border-white/20">
            <div className="flex items-center gap-2 text-white text-xs font-bold mb-1.5">
              <Smartphone size={14} /> Instalar app
            </div>
            <p className="text-white/60 text-xs mb-2">Accede más rápido desde tu celular</p>
            <div className="flex gap-1.5">
              <button onClick={handleInstall} className="flex-1 bg-white text-aqua-700 text-xs font-bold py-1.5 rounded-lg hover:bg-aqua-50 transition-colors">
                Instalar
              </button>
              <button onClick={dismissInstall} className="text-white/40 text-xs px-2 hover:text-white/60 transition-colors">
                No
              </button>
            </div>
          </div>
        )}

        {/* Notificaciones */}
        {permission === 'default' && (
          <div className="mx-3 mb-2">
            <button
              onClick={requestPermission}
              className="w-full flex items-center gap-2 text-white/60 hover:text-white text-xs font-semibold py-2 px-3 rounded-xl hover:bg-white/10 transition-colors"
            >
              <Bell size={13} /> Activar notificaciones
            </button>
          </div>
        )}

        <div className="p-4 border-t border-white/15">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center text-white font-bold text-sm">
              {user?.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">{user?.nombre}</div>
              <div className="text-xs text-white/40">
                {user?.sede === 'TORO' ? <span className="sede-toro">Toro</span> : <span className="sede-pereira">Pereira</span>}
              </div>
            </div>
            <button onClick={logout} className="text-white/50 hover:text-white p-1 transition-colors"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-aqua-900/40 lg:hidden" onClick={() => setOpen(false)} />}

      <main className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        {/* Offline/Update banner */}
        <OfflineBanner pendingCount={pendingCount} />

        <header className="h-15 bg-white border-b border-aqua-100 shadow-aqua-sm flex items-center justify-between px-5 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-lg hover:bg-aqua-50" onClick={() => setOpen(true)}>
              <Menu size={20} className="text-gray-600" />
            </button>
          </div>

          {/* Widget fichaje */}
          <div className="flex items-center gap-2">
            {!asistenciaHoy?.entrada ? (
              <button
                onClick={() => mutEntrada.mutate()}
                disabled={mutEntrada.isPending}
                className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                title="Registrar entrada"
              >
                <Clock size={13} />
                Entrada
              </button>
            ) : !asistenciaHoy?.salida ? (
              <button
                onClick={() => mutSalida.mutate()}
                disabled={mutSalida.isPending}
                className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                title="Registrar salida"
              >
                <Clock size={13} />
                Salida
              </button>
            ) : (
              <span className="flex items-center gap-1.5 bg-gray-100 text-gray-500 text-xs font-bold px-3 py-1.5 rounded-lg">
                <Clock size={13} />
                Turno OK
              </span>
            )}
            <span className={user?.sede === 'TORO' ? 'sede-toro' : 'sede-pereira'}>{sedeLabel}</span>
            <span className="text-sm font-bold text-gray-400" id="colab-hora"></span>
          </div>
        </header>

        <div className="flex-1 p-4 md:p-5 overflow-y-auto">
          <Suspense fallback={<ContentLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Widget flotante Pulpín — Fase 1 */}
      <PulpinWidget />
    </div>
  )
}
